import { Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useLazyQuery } from "@apollo/client";
import { useSetRecoilState, useRecoilValue } from "recoil";

import { FeedItem } from "./FeedItem";
import { TClip } from "MVVM/@types";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { analytics } from "MVVM/Analytics";
import { EmptyList } from "../EmptyList";
import { GET_CLIP } from "MVVM/GraphQL/clip";
import { ViewableClip, LoadingClipId } from "MVVM/State/clip";

interface FeedProps {
  feedData?: {
    clipsRecommended: TClip[];
  };
  feedError?: Error | null;
  feedLoading?: boolean;
}

const Feed = ({ feedData, feedError, feedLoading }: FeedProps) => {
  const currentClip = useRecoilValue(ViewableClip);
  const setViewableClip = useSetRecoilState(ViewableClip);
  const setLoadingClipId = useSetRecoilState(LoadingClipId);
  const [getClip] = useLazyQuery(GET_CLIP, { fetchPolicy: "network-only" });

  const handleClipClick = async (clip: TClip) => {
    try {
      analytics.track("Clip Page - Feed Clip Clicked", {});

      setLoadingClipId(clip.shareId);

      const result = await getClip({ variables: { id: clip.shareId } });

      if (result.data?.clip) {
        setViewableClip(result.data.clip);

        const currentSearch = window.location.search;
        window.history.replaceState(
          {},
          "",
          `/clip/${result.data.clip.shareId}${currentSearch}`,
        );
      }
    } catch (e) {
    } finally {
      setLoadingClipId(null);
    }
  };

  if (feedLoading)
    return (
      <FeedContainer>
        {Array(8)
          .fill("")
          .map((_, index) => (
            <LoadingSkeleton
              height={100}
              style={{ margin: "6px 0" }}
              key={index}
            />
          ))}
      </FeedContainer>
    );

  if (feedError || !feedData)
    return (
      <FeedContainer>
        <Text fontWeight={500}>Something went wrong.</Text>
      </FeedContainer>
    );

  if (!feedData.clipsRecommended.length)
    return <EmptyList text="Nothing else to see" />;

  return (
    <FeedContainer>
      {feedData.clipsRecommended.map((clip: TClip) => {
        const isCurrentClip =
          currentClip && clip.shareId === currentClip.shareId;

        return (
          <FeedItem
            clip={clip}
            key={clip._id}
            active={!!isCurrentClip}
            onClick={isCurrentClip ? undefined : () => handleClipClick(clip)}
          />
        );
      })}
    </FeedContainer>
  );
};

const FeedContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  height: 100%;
  overflow: auto;
  padding: 12px;
  width: 100%;
`;

export { Feed };
